import { canonicalSportID, competitorNames } from './nativePreviewModel'

const ODD_EVEN_MARKET_PATTERN = /odd[ /]?(?:or |\/)?even/
const TOTAL_MARKET_PATTERN = /total|over\/under/
const SPREAD_MARKET_PATTERN = /spread|handicap|run line|puck line/
const MARKET_LINE_SUFFIX_PATTERN = /\s+[+−-]?\d+(?:\.\d+)?$/
const LINE_MARKET_PATTERN = /total|over\/under|spread|handicap|run line|puck line/
const ODD_EVEN_LABEL_PATTERN = /odd\/even|odd or even/
const PITCHER_MARKET_PATTERN = /pitcher|strikeout|earned runs|outs recorded/
const BATTER_MARKET_PATTERN = /player|batter|total bases|home run|rbi|hits by|hits, runs/
const PLAYER_STAT_MARKET_PATTERN = /player|passing|rushing|receiving|receptions|rebounds|assists|three pointers|3-pointers|shots on goal/
const SCORER_MARKET_PATTERN = /touchdown scorer|goalscorer|to score|first scorer/
const CARD_OR_CORNER_MARKET_PATTERN = /card|corner/

// MarketClassification / MarketLineOptions / MarketWidgetRouting in the native clients.
export function postedLinePairs(market, event) {
  const label = market.label.toLowerCase()
  const total = !ODD_EVEN_MARKET_PATTERN.test(label) && (['TOTAL', 'TOTALS', 'OVER_UNDER'].includes(market.type.toUpperCase()) || market.metadata?.bet_offer_type_id === '6' || TOTAL_MARKET_PATTERN.test(label))
  const spread = !total && (market.type.toUpperCase() === 'SPREAD' || market.metadata?.bet_offer_type_id === '1' || SPREAD_MARKET_PATTERN.test(label))
  if (!total && !spread) return []
  const groups = new Map()
  for (const outcome of market.outcomes.filter((item) => item.line != null)) {
    const side = outcome.label.replace(MARKET_LINE_SUFFIX_PATTERN, '').trim().toLowerCase()
    groups.set(side, [...(groups.get(side) ?? []), outcome])
  }
  if (groups.size !== 2) return []
  const keys = [...groups.keys()]
  const away = event ? competitorNames(event)[0]?.toLowerCase() : ''
  const primary = total ? keys.find((key) => ['over', 'o'].includes(key)) : keys.find((key) => ['1', 'away'].includes(key) || (away && key.includes(away))) ?? keys[0]
  const secondary = total ? keys.find((key) => ['under', 'u'].includes(key)) : keys.find((key) => key !== primary)
  if (!primary || !secondary) return []
  return groups.get(primary).flatMap((outcome) => {
    if (groups.get(primary).filter((item) => item.line === outcome.line).length !== 1) return []
    const opposing = groups.get(secondary).filter((item) => item.line === (total ? outcome.line : -outcome.line))
    return opposing.length === 1 ? [{ line: outcome.line, total, outcomes: [outcome, opposing[0]] }] : []
  }).sort((a, b) => a.line - b.line)
}

// EventView.marketRow: posted alternate lines always use the line slider; a
// grid style only keeps a multi-outcome market out of the carousel, and only
// on iPhone (Android reads market_style but never applies it).
export function eventMarketPresentation(market, event, style, device = 'ios') {
  const pairs = postedLinePairs(market, event)
  if (pairs.length > 1 && pairs.length * 2 === market.outcomes.length) return { kind: 'slider', pairs }
  const carousel = market.outcomes.length >= 4 && market.outcomes.every((item) => item.line == null)
  return { kind: carousel && (style !== 'grid' || device === 'android') ? 'carousel' : 'grid', pairs: [] }
}

export function balancedLineIndex(pairs) {
  const imbalance = (pair) => pair.outcomes.every((item) => item.odds) ? Math.abs(pair.outcomes[0].odds - pair.outcomes[1].odds) : Infinity
  return pairs.reduce((best, pair, index) => imbalance(pair) < imbalance(pairs[best]) ? index : best, 0)
}

// Keep every priced outcome, including draws; never renormalize a partial market.
export function marketProbabilities(event, freshness) {
  const market = event?.markets?.find(isMoneyline)
  if (!market || !['fixture', 'current', 'replicated'].includes(freshness) || !['OPEN', 'NOT_STARTED', 'STARTED'].includes(event.status) || market.status !== 'OPEN' ||
      ![2, 3].includes(market.outcomes.length) || new Set(market.outcomes.map((item) => item.id)).size !== market.outcomes.length ||
      market.outcomes.some((item) => item.status !== 'OPEN' || !Number.isFinite(item.odds) || item.odds <= 1000)) return []
  const implied = market.outcomes.map((item) => 1000 / item.odds)
  const total = implied.reduce((sum, value) => sum + value, 0)
  return market.outcomes.map((item, index) => ({ id: item.id, label: probabilityLabel(event, item), share: implied[index] / total, market: market.label }))
}

const outcomeTeam = (event, outcome) => outcome.participant_id ? event?.participants?.find((participant) => participant.id === outcome.participant_id) ?? null : null
const sideName = (event, role, index) => event?.participants?.find((participant) => participant.role === role)?.name ?? competitorNames(event)[index]

// MarketProbabilityModel.displayLabel: the outcome's team, else 1 is home, 2 is away and X is the draw.
function probabilityLabel(event, outcome) {
  const team = outcomeTeam(event, outcome)
  if (team) return team.name
  switch (outcome.label.trim().toUpperCase()) {
    case '1': return sideName(event, 'home', 1) ?? outcome.label
    case '2': return sideName(event, 'away', 0) ?? outcome.label
    case 'X': return 'Draw'
    default: return outcome.label
  }
}

// OutcomeTeamIdentity.teamLabel: a positional 1 or 2 takes its team's name, the
// X between the two teams reads Draw (soccer) or Tie, and Kambi's "A || B" reads "A or B".
export function teamLabel(event, outcome, market) {
  const token = outcome.label.trim().toUpperCase()
  const team = outcomeTeam(event, outcome)
  if ((token === '1' || token === '2') && team) return team.name
  if (token === 'X' && !team && market.outcomes.length === 3 && new Set(market.outcomes.map((item) => outcomeTeam(event, item)?.id).filter(Boolean)).size === 2) {
    return canonicalSportID(event.sport) === 'FOOTBALL' ? 'Draw' : 'Tie'
  }
  return outcome.label.replaceAll(' || ', ' or ')
}

export function teamLabeledMarket(event, market) {
  const outcomes = market.outcomes.map((outcome) => ({ ...outcome, label: teamLabel(event, outcome, market) }))
  return outcomes.some((outcome, index) => outcome.label !== market.outcomes[index].label) ? { ...market, outcomes } : market
}

export function isMoneyline(market) {
  const label = market.label.toLowerCase()
  const type = market.type.toUpperCase()
  if (['TOTAL', 'TOTALS', 'OVER_UNDER', 'SPREAD'].includes(type) || ['1', '6'].includes(market.metadata?.bet_offer_type_id) || LINE_MARKET_PATTERN.test(label)) return false
  return ['MONEYLINE', 'MONEY'].includes(type) || ['moneyline', 'match result', 'match winner', 'regular time', 'bout odds', 'full time', '1x2'].some((prefix) => label.startsWith(prefix))
}


// Same ordering and category membership as EventMarketOrdering / DynamicMarketClassifier.
// First-market opening must refer to the market the apps actually place first.
const eventSections = ['scoring', 'batter_props', 'pitcher_props', 'player_props', 'spreads', 'totals', 'team_markets', 'cards_and_corners', 'periods', 'props']
const periodPattern = /\b(?:quarter|period|set|game|round|inning)\s+\d+\b|\b(?:\d+(?:st|nd|rd|th)\s+(?:half|quarter|period|inning|set|round)|first\s+\d+\s+innings|half[ -]?time)\b|\b\d{1,2}:\d{2}-\d{1,2}:\d{2}\b/
export const isTotal = (market) => !ODD_EVEN_LABEL_PATTERN.test(market.label.toLowerCase()) && (['TOTAL', 'TOTALS', 'OVER_UNDER'].includes(market.type.toUpperCase()) || market.metadata?.bet_offer_type_id === '6' || TOTAL_MARKET_PATTERN.test(market.label.toLowerCase()))
const isSpread = (market) => !isTotal(market) && (market.type.toUpperCase() === 'SPREAD' || market.metadata?.bet_offer_type_id === '1' || SPREAD_MARKET_PATTERN.test(market.label.toLowerCase()))

function eventSection(market, sport) {
  const label = market.label.toLowerCase()
  if (periodPattern.test(label)) return 'periods'
  if (sport === 'BASEBALL') {
    if (PITCHER_MARKET_PATTERN.test(label)) return 'pitcher_props'
    if (BATTER_MARKET_PATTERN.test(label)) return 'batter_props'
  }
  if (PLAYER_STAT_MARKET_PATTERN.test(label)) return 'player_props'
  if (SCORER_MARKET_PATTERN.test(label) && !label.includes('both teams')) return 'scoring'
  if (label.includes('team total') || label.includes('total') && label.includes(' by ')) return 'team_markets'
  if (CARD_OR_CORNER_MARKET_PATTERN.test(label)) return 'cards_and_corners'
  if (isSpread(market)) return 'spreads'
  if (isTotal(market)) return 'totals'
  if (isMoneyline(market)) return 'popular'
  return 'props'
}

export function eventPreviewTabs(event) {
  const sport = (event?.sport || '').toUpperCase()
  const classified = (event?.markets || []).filter((market) => market.outcomes.length).map((market) => ({
    market, section: eventSection(market, sport),
    priority: market.metadata?.tags?.includes('MAIN') ? -2 : market.metadata?.tags?.includes('MAIN_LINE') ? -1 : market.metadata?.sort_order ?? Number.MAX_SAFE_INTEGER,
  }))
  if (!classified.length) return []
  const excluded = ['periods', 'team_markets', 'player_props', 'batter_props', 'pitcher_props', 'scoring', 'cards_and_corners']
  const candidates = classified.filter(({ market, section }) => !excluded.includes(section) && !market.label.toLowerCase().includes('only') && !market.label.includes('&'))
  const main = [isMoneyline, isSpread, isTotal].flatMap((matches) => candidates.filter(({ market }) => matches(market)).toSorted((a, b) => a.priority - b.priority).slice(0, 1))
  if (['FOOTBALL', 'SOCCER'].includes(sport)) {
    for (const label of ['double chance', 'both teams to score', 'tie no bet', 'draw no bet']) {
      const item = candidates.find(({ market }) => market.label.toLowerCase() === label)
      if (item) main.push(item)
    }
  }
  const rank = (item) => main.includes(item) ? main.indexOf(item) : 10 + (eventSections.includes(item.section) ? eventSections.indexOf(item.section) : eventSections.length)
  const ordered = classified.toSorted((a, b) => rank(a) - rank(b) || a.priority - b.priority)
  const tabs = [{ key: 'all', markets: ordered.map((item) => item.market), main: main.map((item) => item.market), sections: new Map(classified.map((item) => [item.market.id, item.section])) }]
  if (main.length) tabs.push({ key: 'popular', markets: main.map((item) => item.market) })
  for (const key of eventSections) {
    const markets = ordered.filter((item) => item.section === key && (key !== 'props' || !isMoneyline(item.market))).map((item) => item.market)
    if (markets.length) tabs.push({ key, markets })
  }
  const combinable = ordered.map((item) => item.market).filter((market) => market.metadata?.tags?.includes('BET_BUILDER') && market.status === 'OPEN' && market.outcomes.some((outcome) => outcome.status === 'OPEN' && outcome.odds != null))
  if (combinable.length) tabs.push({ key: 'same_game_parlay', markets: combinable })
  return tabs
}

// MarketCategory titles and EventMarketOrdering.title: what the apps print on tabs and group headers.
const NATIVE_TITLES = { all: 'All Markets', popular: 'Main Markets', spreads: 'Spreads & Lines', totals: 'Totals', props: 'Game Props', player_props: 'Player Props', batter_props: 'Batter Props', pitcher_props: 'Pitcher Props', cards_and_corners: 'Cards & Corners', team_markets: 'Team Markets', periods: 'Periods', scoring: 'Scoring', same_game_parlay: 'Same Game Parlay' }

export function nativeCategoryTitle(key, sport = '') {
  const upper = sport.toUpperCase()
  if (key === 'spreads') {
    if (upper === 'BASEBALL') return 'Run Lines'
    if (['HOCKEY', 'ICE_HOCKEY'].includes(upper)) return 'Puck Lines'
  }
  if (key === 'periods') {
    if (upper === 'BASEBALL') return 'Innings'
    if (['TENNIS', 'TABLE_TENNIS', 'VOLLEYBALL'].includes(upper)) return 'Sets & Games'
    if (['NFL', 'AMERICAN_FOOTBALL', 'BASKETBALL'].includes(upper)) return 'Halves & Quarters'
    if (['SOCCER', 'FOOTBALL'].includes(upper)) return 'Halves'
    if (['MMA', 'MARTIAL_ARTS', 'BOXING'].includes(upper)) return 'Rounds'
  }
  return NATIVE_TITLES[key] ?? key
}

export function nativeTabTitle(tab, sport) {
  return tab.key === 'all' ? `${NATIVE_TITLES.all} (${tab.markets.length})` : nativeCategoryTitle(tab.key, sport)
}

// EventView.marketGroups: the All tab leads with Main Markets, then each
// category in the order its first market appears; any other tab is one group.
export function eventMarketGroups(tabs, key) {
  const tab = tabs.find((item) => item.key === key)
  if (!tab) return []
  if (key !== 'all') return [{ key, markets: tab.markets }]
  const mainIds = new Set(tab.main.map((market) => market.id))
  const rest = new Map()
  for (const market of tab.markets.filter((item) => !mainIds.has(item.id))) {
    const section = tab.sections.get(market.id)
    rest.set(section, [...(rest.get(section) ?? []), market])
  }
  return [...(tab.main.length ? [{ key: 'popular', markets: tab.main }] : []), ...[...rest].map(([section, markets]) => ({ key: section, markets }))]
}
